import { Box, Button, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import React, { useEffect, useMemo, useState }  from 'react'
import { useNavigate } from 'react-router-dom';
import CreateNewProjectButton from './CreateNewProjectButton';
import SearchBar from '../SearchBar';
import axios from 'axios';
import useCurrentUser from '../../hooks/useCurrentUser';


const formatDate = (isoString) => {
  const date = new Date(isoString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const filterButtons = [
  { label: 'Last Modified', value: 'created_at', color: '#16a4b0' },
  { label: 'Name', value: 'name', color: '#16a4b0' },
];

const Projects = () => {

  const [projects, setProjects] = useState([]);
  const navigate = useNavigate();
  const { permissions } = useCurrentUser();
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [currentPage, setCurrentPage] = useState(0);
  const [projectsPerPage, setProjectsPerPage] = useState(10);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get('/projects');
        setProjects(response.data);
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };
        fetchProjects();
    }, []);


    const filteredProjects = useMemo(() => 
        projects.filter(p => p.name.toLowerCase().includes(searchText.toLowerCase())),
        [projects, searchText]
        );

    const sortedProjects = useMemo(() =>
        [...filteredProjects].sort((a, b) =>
            sortBy === 'name'
            ? a.name.localeCompare(b.name)
            : new Date(b.created_at) - new Date(a.created_at)
        ),
        [filteredProjects, sortBy]
        );

    const paginatedProjects = useMemo(() =>
        sortedProjects.slice(currentPage * projectsPerPage, (currentPage + 1) * projectsPerPage),
        [sortedProjects, currentPage, projectsPerPage]
        );

    const pageCount = useMemo(() => 
        Math.ceil(sortedProjects.length / projectsPerPage), 
        [sortedProjects, projectsPerPage]
        );


  return (
    <Box sx={{ mx: 'auto', mt: 1, borderRadius: 4, overflow: 'hidden', width: { xs: '80%', sm: '70%' }, display: 'flex', flexDirection: 'column', p: 1 }}>
        <Typography variant="h3" fontWeight={700} color='black' mt={4}>
            Projects
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, mt: 2 }}>
            <Typography variant="h6" color="rgb(158, 151, 151)">
            Manage your projects or create a new one to get started. 
            </Typography>
            <Box sx={{ ml: 'auto', transform: 'scale(1.2)', transformOrigin: 'right' }}>
            <CreateNewProjectButton disabled={!permissions.includes('create_project')} />
            </Box>
        </Box>

        <Box mt={4} sx={{ width: '100%' }}>
            <SearchBar searchText={searchText} setSearchText={setSearchText} placeholder="Search projects..." />
        </Box>

        <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
            {filterButtons.map((btn) => (
                <Button
                key={btn.value}
                onClick={() => { setSortBy(btn.value); setCurrentPage(0); }}
                variant={sortBy === btn.value ? 'contained' : 'outlined'}
                sx={{
                    borderRadius: '12px',
                    borderColor: "#cccccc",
                    color: sortBy === btn.value ? 'white' : 'black',
                    backgroundColor: sortBy === btn.value ? btn.color : 'transparent',
                    textTransform: 'none',
                    fontSize: '16px',
                }}
                >
                {btn.label}
                </Button>
            ))}
            </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mb:2  }}>
            {[5, 10, 15].map((num) => (
            <Button
                key={num}
                onClick={() => { setProjectsPerPage(num); setCurrentPage(0); }}
                variant={projectsPerPage === num ? 'contained' : 'outlined'}
                sx={{
                borderRadius: '12px',
                borderColor: "#cccccc",
                color: projectsPerPage === num ? 'white' : 'black',
                backgroundColor: projectsPerPage === num ? '#945092' : 'transparent',
                textTransform: 'none',
                fontSize: '16px',
                minWidth: '40px',
                padding: '4px 8px',
                }}
            >
                {num}
            </Button>
            ))}
        </Box>

        <TableContainer sx={{ mt: 0, borderRadius: '12px', border: '1px solid #cccccc' }}>
            <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Name</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Description</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Last Modified</TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {paginatedProjects.map((project) => (
                        <TableRow
                            key={project.id}
                            hover
                            onClick={() => navigate(`/projects/${project.id}/trainings`)}
                            sx={{
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: '#f5f5f5' },
                            }}
                        >
                            <TableCell sx={{ py: 1.3, fontSize: '1.1rem' }}>{project.name}</TableCell>
                            <TableCell sx={{ py: 1.3, color: 'text.secondary', fontSize: '1.1rem' }}>{project.info.description}</TableCell>
                            <TableCell sx={{ py: 1.3, color: 'text.secondary', fontSize: '1.1rem' }}>{formatDate(project.created_at)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, gap: 2 }}>
            <Button
                variant="outlined"
                disabled={currentPage === 0}
                onClick={() => setCurrentPage(prev => prev - 1)}
                sx={{ borderRadius: '12px', textTransform: 'none', color: '#945092', borderColor: '#945092' }}
            >
                Previous
            </Button>
            <Typography variant="body1" sx={{ alignSelf: 'center' }}>
                Page {currentPage + 1} of {pageCount}
            </Typography>
            <Button
                variant="outlined"
                disabled={currentPage >= pageCount - 1}
                onClick={() => setCurrentPage(prev => prev + 1)}
                sx={{ borderRadius: '12px', textTransform: 'none', color: '#945092', borderColor: '#945092' }}
            >
                Next
            </Button>
        </Box>
    </Box>
  )
}

export default Projects
